import AWS from 'aws-sdk';
import { useEffect, useMemo, useState } from "react";
import { useDispatchReducerContext, useStateGettersContext } from '../../store';
import { cb2Promise, TEST_ENV_LOADBALANCER_ARN, useGetAssumedKeysForBuildAccount, useGetAssumedKeysForDeployAccounts, useHandleAwsAuthErr } from '../../common/AwsUtil';
import { SHOW_TOAST } from '../../store/dispatchNames';

export default function useSkipTestEnv(projectName, owner, projectShortName, pipetype) {
    const dispatch = useDispatchReducerContext();
    const [isSkipTestDeployment, setIsSkipTestDeployment] = useState(null);
    const [isSkipTestLoading, setIsSkipTestLoading] = useState(null);
    const [hasSkipPermission, setHasSkipPermission] = useState(null);
    const handleAwsAuthErr = useHandleAwsAuthErr();
    const storeGetters = useStateGettersContext();

    useEffect(() => {
        if (projectName) {
            let didMount = true;
            const ssm = new AWS.SSM(storeGetters.readonlyKeys);
            setIsSkipTestLoading(true);
            cb2Promise(ssm, 'getParameter', { Name: `/skip-stage/${projectName}/test` }).then((data) => {
                if (!didMount) return;
                setHasSkipPermission(true);
                setIsSkipTestDeployment(data?.Parameter?.Value === 'True');
            }).catch((err) => {
                if (!handleAwsAuthErr(err)) {
                    if (err.name === 'AccessDenied') {
                        setHasSkipPermission(false);
                    }
                }

                if (!didMount) return;
            }).finally(() => {
                if (!didMount) return;
                setIsSkipTestLoading(false);
            });

            return () => didMount = false;
        }
    }, [projectName, storeGetters.readonlyKeys, handleAwsAuthErr]);

    const getAssumedKeys = useGetAssumedKeysForBuildAccount();

    const handleSkipTestDeploymentChange = async (event) => {
        if (projectName && owner) {
            let didMount = true;
            setIsSkipTestLoading(true);
            const params = await getAssumedKeys(owner);
            const ssm = new AWS.SSM(params);
            const checked = !isSkipTestDeployment;
            cb2Promise(ssm, 'putParameter', {
                Name: `/skip-stage/${projectName}/test`,
                Value: checked ? 'True' : 'False',
                Overwrite: true,
            }).then(() => {
                setIsSkipTestDeployment(checked);
            }).catch((err) => {
                if (!didMount) return;
                if (!handleAwsAuthErr(err)) {
                    dispatch({
                        type: SHOW_TOAST,
                        payload: {
                            message: err.message,
                            severity: 'error'
                        }
                    });
                }

            }).finally(() => {
                if (!didMount) return;
                setIsSkipTestLoading(false);
            });
        }
    }

    const getAssumedKeysForDeployAccounts = useGetAssumedKeysForDeployAccounts();

    const [loadTrigger, setLoadTrigger] = useState(true);

    useEffect(() => {
        const interval = setInterval(() => {
            setLoadTrigger((flag) => !flag);
        }, 15000);

        return () => {
            clearInterval(interval);
        }
    }, [])

    const [hasInitDestroyTestEnvStatus, setHasInitDestroyTestEnvStatus] = useState(false);
    const [hasDestroyTestEnvPermission, setHasDestroyTestEnvPermission] = useState(false);
    const [isDestroyingTestEnv, setIsDestroyingTestEnv] = useState(false);
    const [isDestroyTestEnvDisabled, setIsDestroyTestEnvDisabled] = useState(true);

    const isDisplayDestroyTestEnv = useMemo(() => {
        return hasDestroyTestEnvPermission && pipetype !== 'ui' && pipetype !== 'application' && hasInitDestroyTestEnvStatus;

    }, [hasDestroyTestEnvPermission, pipetype, hasInitDestroyTestEnvStatus]);

    const deleteRuleArnIsExist = async () => {
        const params = await getAssumedKeysForDeployAccounts(owner, 'test', 'w');
        const elb = new AWS.ELBv2(params);
        const ruleName = `${projectShortName}-tg-test`;


        const listenersData = await cb2Promise(elb, 'describeListeners', {
            LoadBalancerArn: TEST_ENV_LOADBALANCER_ARN,
        });

        const listener = listenersData.Listeners.find(item => {
            return item.Port === 443;
        });

        const listenerArn = listener?.ListenerArn;

        if (listenerArn) {
            const rulesData = await cb2Promise(elb, 'describeRules', {
                ListenerArn: listenerArn,
            })

            const rule = rulesData.Rules.find(item => {
                return (item?.Actions?.[0]?.TargetGroupArn ?? '').includes(ruleName)
            });

            const RuleArn = rule?.RuleArn;
            
            await cb2Promise(elb, 'deleteRule', {
                RuleArn
            })
        }
    }

    const destroyTestEnv = async () => {
        if (projectName && owner && pipetype) {
            setIsDestroyingTestEnv(true);
            
            const params = await getAssumedKeysForDeployAccounts(owner, 'test', 'w');
            const cloudformation = new AWS.CloudFormation(params);

            let projectType = '';
            let newProjectName = projectName;
            if (pipetype === 'lambda') {
                projectType = 'lambda'
            } else if (pipetype === 'infrastructure'){
                projectType = 'infra';
                newProjectName = newProjectName.replace('-infrastructure', '');
            } else {
                projectType = 'awsinfra';
            }

            try {
                if (pipetype === 'infrastructure') {
                    await deleteRuleArnIsExist();
                }
                const StackName = `${newProjectName}-test-${projectType}-stack`;
                await cb2Promise(cloudformation, 'deleteStack', {
                    StackName,
                })
                dispatch({
                    type: SHOW_TOAST,
                    payload: {
                        message: 'Successed',
                        severity: 'success'
                    }
                });
            } catch (err) {
                if (!handleAwsAuthErr(err)) {
                    dispatch({
                        type: SHOW_TOAST,
                        payload: {
                            message: err.message,
                            severity: 'error'
                        }
                    });
                }
            }
        }
    }

    useEffect(() => {
        setHasInitDestroyTestEnvStatus(false);
    }, [projectName]);

    const [testEnvStackStatus, setTestEnvStackStatus] = useState();

    useEffect(() => {
        if (!projectName || !owner || !pipetype || pipetype === 'ui' || pipetype === 'application') return;
        let didMount = true;
        const fetchDestroyTestEnvStatus = async () => {
            let cloudformation;
            try {
                const params = await getAssumedKeysForDeployAccounts(owner, 'test', 'r');
                cloudformation = new AWS.CloudFormation(params);
            } catch (err) {
                if (err.name === 'AccessDenied') {
                    setHasDestroyTestEnvPermission(false);
                }
            }

            if (!cloudformation) return;

            let projectType = '';
            let newProjectName = projectName;
            if (pipetype === 'lambda') {
                projectType = 'lambda'
            } else if (pipetype === 'infrastructure'){
                projectType = 'infra';
                newProjectName = newProjectName.replace('-infrastructure', '');
            } else {
                projectType = 'awsinfra';
            }

            const StackName = `${newProjectName}-test-${projectType}-stack`;

            cb2Promise(cloudformation, 'describeStacks', {
                StackName,
            }).then((data) => {
                if (!didMount) return;
                const stackStatus = data?.Stacks?.[0].StackStatus;
                setTestEnvStackStatus(stackStatus);

                if (stackStatus === 'DELETE_IN_PROGRESS') {
                    setIsDestroyingTestEnv(true);
                    setIsDestroyTestEnvDisabled(true);
                } else if (['DELETE_COMPLETE', 'CREATE_IN_PROGRESS', 'REVIEW_IN_PROGRESS', 'DELETE_FAILED'].includes(stackStatus) || stackStatus === undefined) {
                    setIsDestroyingTestEnv(false);
                    setIsDestroyTestEnvDisabled(true);
                } else {
                    setIsDestroyingTestEnv(false);
                    setIsDestroyTestEnvDisabled(false);
                }

                setHasDestroyTestEnvPermission(true);

            }).catch((err) => {
                if (!didMount) return;
                if (!handleAwsAuthErr(err)) {
                    if (err.message.includes('does not exist')) {
                        setHasDestroyTestEnvPermission(true);
                        setIsDestroyingTestEnv(false);
                        setIsDestroyTestEnvDisabled(true);
                    } else if (err.name === 'AccessDenied') {
                        setHasDestroyTestEnvPermission(false);
                    }
                }

            }).finally(() => {
                setHasInitDestroyTestEnvStatus(true);
            });
        }
        fetchDestroyTestEnvStatus();

        return () => didMount = false;
    }, [getAssumedKeysForDeployAccounts, owner, pipetype, projectName, loadTrigger, handleAwsAuthErr]);


    return { 
        hasSkipPermission, isSkipTestLoading, isSkipTestDeployment, handleSkipTestDeploymentChange,
        isDisplayDestroyTestEnv, isDestroyTestEnvDisabled, isDestroyingTestEnv, destroyTestEnv, testEnvStackStatus
    };
}